import { Link, useLocation } from '@tanstack/react-router';
import { Menu } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';
import { type StudioDetails } from '~entities/studios/model/types';

import { type ParentFormValues } from '~features/contacts/parent/parent-schema';
import { AddFamilyDialog } from '~features/dashboard/ui/add-family-dialog';
import { ContactSearchDialog } from '~features/parents/ui/contact-search-dialog';
import { ManualParentEntryDialog } from '~features/parents/ui/manual-parent-entry-dialog';
import { UserProfileMenu } from '~features/profile/ui/user-menu';
import { AddStudentDialog } from '~features/students/ui/add-student-dialog';

import { Button } from '~shared/shadcn/ui/button';
import { cn } from '~shared/shadcn/lib/utils';
import { copyToClipboard } from '~shared/utils/clipboard';
import { buildStudioLoginUrl } from '~shared/utils/studio-portal-url';

import { MobileNavSheet } from './navbar/mobile-nav-sheet';
import { NavSection } from './navbar/nav-section';
import { useDashboardNavMenus } from './navbar/use-dashboard-nav-menus';
import { isActiveSection } from './navbar/utils';

/** Enrollio logo as home link to /dashboard (ENR-3525). */
const EnrollioHomeLogo = ({ className }: { className?: string }) => (
  <Link to='/dashboard' className={className} aria-label='Enrollio home'>
    <img
      className='h-5'
      src='https://storage.googleapis.com/msgsndr/VGZndtOFHrO4NIELBQYo/media/672ba39a6e8a940606bd75a8.svg'
      alt='Enrollio logo'
    />
  </Link>
);

interface TopNavBarProps {
  studioData?: StudioDetails;
  isStudioFetching?: boolean;
}

export const TopNavBar = ({ studioData }: TopNavBarProps) => {
  const { pathname } = useLocation();

  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isAddFamilyDialogOpen, setIsAddFamilyDialogOpen] = useState(false);
  const [isContactSearchOpen, setIsContactSearchOpen] = useState(false);
  const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);
  const [selectedContactData, setSelectedContactData] =
    useState<Partial<ParentFormValues> | null>(null);
  const [isAddStudentDialogOpen, setIsAddStudentDialogOpen] = useState(false);

  const studioDetailsQuery = useGetStudioDetails({
    enabled: !studioData,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const data = studioData ?? studioDetailsQuery.data;

  const copyLink = async (
    url: string,
    id: string,
    description = 'Link has been copied to clipboard.',
  ) => {
    try {
      const success = await copyToClipboard(url, id);
      if (success) {
        toast.success('Link copied!', { description });
      } else {
        toast.error('Failed to copy');
      }
    } catch {
      toast.error('Failed to copy');
    }
  };

  const copyRegistrationFormLink = () => {
    const locationId = data?.locationId;
    if (!locationId) {
      toast.error('Studio ID not available');
      return;
    }

    const url = `${import.meta.env.VITE_PARENT_PORTAL_URL}/create-parent?locationId=${locationId}`;

    void copyLink(
      url,
      'copy-registration-form-link',
      'Parent registration form link has been copied to clipboard.',
    );
  };

  const copyStudioLoginLink = () => {
    const locationId = data?.locationId;
    if (!locationId) {
      toast.error('Studio ID not available');
      return;
    }

    void copyLink(
      buildStudioLoginUrl(locationId),
      'copy-studio-login-link',
      'Studio login link has been copied to clipboard.',
    );
  };

  const copyParentPortalLink = () => {
    const locationId = data?.locationId;
    if (!locationId) {
      toast.error('Studio ID not available');
      return;
    }

    const url = `${import.meta.env.VITE_PARENT_PORTAL_URL}/login?studioId=${locationId}`;

    void copyLink(
      url,
      'copy-parent-portal-link',
      'Parent portal link has been copied to clipboard.',
    );
  };

  const navMenus = useDashboardNavMenus({
    studioData: data,
    onAddStudent: () => {
      setIsAddStudentDialogOpen(true);
    },
    onAddParent: () => {
      setIsAddFamilyDialogOpen(true);
    },
    copyRegistrationFormLink,
    copyStudioLoginLink,
    copyParentPortalLink,
  });

  const { primaryMenus, endMenus } = useMemo(() => {
    const primary = navMenus.filter((menu) => menu.align !== 'end');
    const end = navMenus.filter((menu) => menu.align === 'end');
    return { primaryMenus: primary, endMenus: end };
  }, [navMenus]);

  const renderMenu = (menu: (typeof navMenus)[number]) => {
    if (menu.to && menu.sections.length === 0) {
      const active = isActiveSection(pathname, menu.sectionKey);
      return (
        <Button
          key={menu.sectionKey}
          variant='ghost'
          asChild
          className={cn(
            // h-full + after:bottom-0 keeps the active bar flush with the
            // header bottom border (0px gap) without overflowing content (ENR-3910).
            'relative h-full rounded-none px-3 text-[13px] font-medium tracking-[-0.01em]',
            'text-slate-700 transition-colors hover:bg-slate-100/80 hover:text-slate-950',
            active &&
              'font-semibold text-slate-950 after:absolute after:inset-x-2.5 after:bottom-0 after:h-[3px] after:rounded-full after:bg-[#FFC300]',
          )}
        >
          <Link to={menu.to} aria-current={active ? 'page' : undefined}>
            {menu.label}
          </Link>
        </Button>
      );
    }

    return (
      <NavSection
        key={menu.sectionKey}
        label={menu.label}
        sectionKey={menu.sectionKey}
        pathname={pathname}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        columns={menu.columns}
        widthClassName={menu.widthClassName}
        align={menu.align}
        sections={menu.sections}
      />
    );
  };

  return (
    <>
      <div
        data-enrollio-top-nav
        className='sticky top-0 z-50 w-full min-w-0 overflow-visible border-b border-border/30 bg-white/80 backdrop-blur-2xl supports-[backdrop-filter]:bg-white/70'
      >
        <div className='relative mx-auto flex h-12 w-full min-w-0 items-center gap-3 overflow-visible px-1 md:px-4'>
          <div className='flex shrink-0 items-center gap-3'>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='shrink-0 min-[1100px]:hidden'
              aria-label='Open menu'
              onClick={() => {
                setMobileNavOpen(true);
              }}
            >
              <Menu className='size-5' />
            </Button>
            <EnrollioHomeLogo className='hidden shrink-0 min-[1100px]:block' />
          </div>

          <EnrollioHomeLogo className='absolute left-1/2 -translate-x-1/2 min-[1100px]:hidden' />

          <nav className='hidden h-full min-w-0 flex-1 items-stretch gap-0.5 min-[1100px]:flex'>
            {primaryMenus.map(renderMenu)}
          </nav>

          <div className='ml-auto flex h-full shrink-0 items-center gap-0.5'>
            <div className='hidden h-full items-stretch gap-0.5 min-[1100px]:flex'>
              {endMenus.map(renderMenu)}
            </div>
            {/* Single instance so a hidden duplicate can't steal positioning */}
            <UserProfileMenu />
          </div>
        </div>
      </div>

      <MobileNavSheet
        open={mobileNavOpen}
        onOpenChange={setMobileNavOpen}
        menus={navMenus}
      />

      {isAddFamilyDialogOpen && (
        <AddFamilyDialog
          open={isAddFamilyDialogOpen}
          onOpenChange={setIsAddFamilyDialogOpen}
          onContactSearchOpen={setIsContactSearchOpen}
          onManualEntryOpen={setIsManualEntryOpen}
        />
      )}

      {isManualEntryOpen && (
        <ManualParentEntryDialog
          open={isManualEntryOpen}
          onOpenChange={setIsManualEntryOpen}
          studioId={data?.locationId ?? ''}
          initialData={selectedContactData}
        />
      )}

      {isContactSearchOpen && (
        <ContactSearchDialog
          open={isContactSearchOpen}
          onOpenChange={setIsContactSearchOpen}
          onContactSelect={(contactData) => {
            setSelectedContactData(contactData);
            setIsManualEntryOpen(true);
          }}
        />
      )}

      {isAddStudentDialogOpen && (
        <AddStudentDialog
          open={isAddStudentDialogOpen}
          onOpenChange={setIsAddStudentDialogOpen}
        />
      )}
    </>
  );
};
